//@flow

import React, { useEffect, useContext, useState } from "react";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useField } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { useTranslation } from "react-i18next";

import { read, readAndReplace } from "modules/ChangeServiceForm/misc/utilities";

import { fetchProvidersByCategoryId } from "redux/actions/providers";
import { fetchTariffs } from "redux/actions/tariffs";

import { getProviders, getTariffs } from "redux/sagas/selectors";

import { mapProviders } from "Commons/Components/FormComponents/misc/Mappers";
import { PROVIDER_IS_NOT_IN_LIST } from "Commons/Modules/Provider/misc/Constants";
import Provider from "./Provider";
import Tariff from "./Tariff";
import Comment from "./Comment";
import ChangeServiceFormSharedContext from "../../../misc/context";
import { Row, Label, Space } from "../../shared/CommonComponents";
import { mapTariffs } from "./helpers";

const ProvidersAndTariffsHandler = () => {
  const { t } = useTranslation("common");
  const {
    category: { categoryType },
    inheritedStyles: { formRow, formLabelLight, hidden },
    configurationData: {
      translations: { fieldLabels, glossary },
    },
    activeCategory,
    initialValues,
    steps: { stepName },
  } = useContext(ChangeServiceFormSharedContext);
  const dispatch = useDispatch();
  const providers = useSelector(getProviders);
  const tariffs = useSelector(getTariffs);

  const NOT_IN_LIST_OPTION = { value: PROVIDER_IS_NOT_IN_LIST, text: t("notInList") };

  /* Company type detection */
  const propertyTypeField = useField("propertyType");
  const isCompany = propertyTypeField?.input.value === "COMPANY" || false;

  /* Pre-selections */
  const providerIdPreSelection = initialValues[categoryType].categoryProviderId;
  const tariffIdPreSelection = initialValues[categoryType].permanentTariffId;
  const providerIdField = useField("categoryProviderId");
  const currentProviderName = useField("currentProviderName");
  const tariffIdField = useField("permanentTariffId");
  const currentTariff = useField("currentTariff");
  const currentTariffName = useField("currentTariffName");
  const comment = useField("comment");

  const [selectedProvider, setSelectedProvider] = useState(
    providers?.providers[activeCategory.id]?.find((provider) => provider.id === providerIdPreSelection)
  );

  const [selectedTariff, setSelectedTariff] = useState(
    tariffs?.tariffs.find((tariff) => tariff.permanentId === tariffIdPreSelection)
  );

  useEffect(() => {
    if (selectedProvider) {
      currentProviderName.input.onChange(selectedProvider.company?.companyName);
    }
  }, [selectedProvider]);

  useEffect(() => {
    if (selectedTariff) {
      currentTariff.input.onChange(selectedTariff.id);
      currentTariffName.input.onChange(selectedTariff.name);
    }
  }, [selectedTariff]);

  useEffect(() => {
    setSelectedProvider(
      providers?.providers[activeCategory.id]?.find((provider) => provider.id === providerIdField.input.value)
    );
  }, [providers]);

  useEffect(() => {
    if (tariffs?.tariffs.length) {
      const _tariff = tariffs?.tariffs.find((tariff) => tariff.permanentId === tariffIdField.input.value);
      if (_tariff) setSelectedTariff(_tariff);
      else {
        tariffIdField.input.onChange(undefined);
        currentTariff.input.onChange(undefined);
        currentTariffName.input.onChange(undefined);
      }
    }
  }, [tariffs]);

  /* Fetch all providers */
  useEffect(() => {
    const getProviders = async () => {
      if (activeCategory) {
        await dispatch(fetchProvidersByCategoryId(activeCategory.id));
        const categoryProvider = {
          categoryProviderId: providerIdPreSelection,
          categoryType,
        };
        dispatch(fetchTariffs(categoryProvider, isCompany));
      }
    };
    getProviders();
  }, [activeCategory]);

  /* Dropdown options */
  const providersOptions = () => {
    if (providers && activeCategory) {
      const _options = providers?.providers[activeCategory.id]?.map(mapProviders);
      if (_options && _options.length) _options.unshift(NOT_IN_LIST_OPTION);
      return _options;
    }
    return [];
  };
  const tariffOptions = tariffs && tariffs.tariffs.length > 0 ? tariffs.tariffs.map(mapTariffs) : [];

  return (
    <>
      <Row className={formRow}>
        <Label className={formLabelLight}>
          {readAndReplace(fieldLabels.providerAndTariff.providerId, {
            replace: (domNode) => {
              if (domNode.attribs && domNode.attribs.id === "replace") {
                return <span>{categoryType && glossary[categoryType.toLowerCase()].trans}</span>;
              }
            },
          })}
        </Label>
        <Provider options={providersOptions()} loading={providers?.isFetching} stepName={stepName} />
      </Row>
      <Row className={classnames(formRow, { [hidden]: providerIdField.input.value === PROVIDER_IS_NOT_IN_LIST })}>
        <Label className={formLabelLight}>
          {readAndReplace(fieldLabels.providerAndTariff.permanentTariffId, {
            replace: (domNode) => {
              if (domNode.attribs && domNode.attribs.id === "replace") {
                return <span>{categoryType && glossary[categoryType.toLowerCase()].trans}</span>;
              }
            },
          })}
        </Label>
        <Tariff options={tariffOptions} loading={tariffs?.isFetching} stepName={stepName} />
      </Row>
      <Row className={classnames(formRow, { [hidden]: providerIdField.input.value !== PROVIDER_IS_NOT_IN_LIST })}>
        <Label className={formLabelLight}>{read(fieldLabels.providerAndTariff.comment)}</Label>
        <Comment fieldName="comment" stepName={stepName} />
      </Row>
      <Space rem={0.25} />
      <OnChange name={"categoryProviderId"}>
        {(categoryProviderId) => {
          // Reset tariffs
          tariffIdField.input.onChange(undefined);
          currentTariff.input.onChange(undefined);
          currentTariffName.input.onChange(undefined);

          if (categoryProviderId !== PROVIDER_IS_NOT_IN_LIST) {
            comment.input.onChange(undefined);

            setSelectedProvider(
              providers?.providers[activeCategory.id].find((provider) => provider.id === categoryProviderId)
            );
            const categoryProvider = {
              categoryProviderId,
              categoryType,
            };
            // Get new Tariffs
            dispatch(fetchTariffs(categoryProvider, isCompany));
          } else {
            // $FlowFixMe
            setSelectedProvider({ id: PROVIDER_IS_NOT_IN_LIST });
          }
        }}
      </OnChange>
      <OnChange name={"permanentTariffId"}>
        {(permanentTariffId) => {
          setSelectedTariff(tariffs?.tariffs.find((tariff) => tariff.permanentId === permanentTariffId));
        }}
      </OnChange>
    </>
  );
};

export default ProvidersAndTariffsHandler;
